<script setup>
import { computed, ref, onMounted } from 'vue';
import { useAuthentication } from '@/composables/use-authentication';
import VueJitsiMeet from '@/components/VueJitsiMeet.vue';

const emit = defineEmits(['videoConferenceLeft', 'joined', 'participantJoined', 'participantLeft']);

const props = defineProps({
  room: {
    type: String,
    default: undefined,
  },
});

const { currentUser, init: initCurrentUser } = useAuthentication();

const fullName = computed(() => {
  const name = currentUser.value?.name;
  return `${name?.firstName} ${name?.lastName}`;
});
const email = computed(() => currentUser.value?.email);
const picURL = computed(() => currentUser.value?.picURL);

const room = computed(() => props.room);

const jitsiOptions = computed(() => ({
  roomName: room.value,
  parentNode: document.querySelector('#jitsi-mount-point'),
  userInfo: {
    displayName: fullName.value,
    email: email.value,
  },
  configOverwrite: {
    enableNoisyMicDetection: true,
    disablePolls: true,
  },
  interfaceConfigOverwrite: {
    JITSI_WATERMARK_LINK: 'https://parmazip.ca',
    SHOW_JITSI_WATERMARK: false,
    SHOW_WATERMARK_FOR_GUESTS: false,
    SHOW_CHROME_EXTENSION_BANNER: false,
    MOBILE_APP_PROMO: false,
    DISPLAY_WELCOME_PAGE_CONTENT: false,
    SETTINGS_SECTIONS: [],
    CLOSE_PAGE_GUEST_HINT: false,
    TOOLBAR_BUTTONS: [
      'microphone', 
      'camera', 
      'closedcaptions', 
      'desktop', 
      'fullscreen',
      'chat',
      'tileview',
      // 'hangup',
      // 'recording',
      // 'fodeviceselection', 'hangup', 'profile', 'chat', 'recording',
      // 'livestreaming', 'etherpad', 'sharedvideo', 'settings', 'raisehand',
      // 'videoquality', 'filmstrip', 'invite', 'feedback', 'stats', 'shortcuts',
      // 'tileview', 'videobackgroundblur', 'download', 'help', 'mute-everyone',
      // 'e2ee',
    ],
  },
}));

const jitsiRef = ref(null);

// Expose methods to parent component
defineExpose({
  enterPictureInPicture: () => {
    console.log('DoctorMeeting.enterPictureInPicture called');
    console.log('jitsiRef.value:', jitsiRef.value);
    if (jitsiRef.value) {
      jitsiRef.value.togglePictureInPicture();
    } else {
      console.error('jitsiRef.value is null');
    }
  },
  executeCommand: (command, ...args) => {
    if (jitsiRef.value) {
      jitsiRef.value.executeCommand(command, ...args);
    }
  }
});

const participantCount = ref(0);

const events = {
  videoConferenceJoined: (event) => {
    console.log('Video conference joined:', event);
    // Set initial participant count to 1 when pharmacist joins
    participantCount.value = 1;
    emit('joined');
  },
  videoConferenceLeft: (event) => {
    console.log('Video conference left:', event);
    emit('videoConferenceLeft', event);
  },
  participantJoined: (event) => {
    console.log('Participant joined:', event);
    participantCount.value++;
    emit('participantJoined', { 
      id: event.id, 
      displayName: event.displayName,
      totalParticipants: participantCount.value
    });
  },
  participantLeft: (event) => {
    console.log('Participant left:', event);
    participantCount.value = Math.max(0, participantCount.value - 1);
    emit('participantLeft', { 
      id: event.id, 
      displayName: event.displayName,
      totalParticipants: participantCount.value
    });
  },
}

function runCommands () {
  if (!jitsiRef.value) return;
  
  jitsiRef.value.embedJitsiWidget();
  
  // Set user information dynamically
  jitsiRef.value.executeCommand('avatarUrl', picURL.value);
  jitsiRef.value.executeCommand('displayName', fullName.value);
  jitsiRef.value.executeCommand('localSubject', 'Parmazip Virtual Consultation');
  jitsiRef.value.executeCommand('toggleAudio');
  
  // Add event listeners
  for (const eventName in events) {
    jitsiRef.value.addEventListener(eventName, events[eventName]);
  }
}

// function removeJitsi () {
//   jitsiRef.value.removeJitsiWidget();
//   for (const eventName in events) {
//     jitsiRef.value.removeEventListener(eventName, events[eventName]);
//   }
// }

onMounted(async () => {
  try {
    await initCurrentUser();
    
    // Small delay to ensure DOM is ready
    setTimeout(() => {
      runCommands();
    }, 100);
  } catch (error) {
    console.error('Failed to initialize doctor meeting:', error);
  }
});

// onUnmounted(() => {
//   removeJitsi();
// });
</script>

<template>
  <VueJitsiMeet 
    v-if="room" 
    domain="jitsi.parmazip.com" 
    ref="jitsiRef" 
    :options="jitsiOptions" 
  />
  <div v-else class="flex items-center justify-center h-full bg-gray-100">
    <div class="text-center p-4">
      <div class="text-gray-500 mb-2">
        <i class="las la-video text-3xl"></i>
      </div>
      <p class="text-gray-600">Waiting for room information...</p>
    </div>
  </div>
</template>
